import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BaseHttpService, HttpResult } from '../../core/services/base-http.service';
import { checkEmailStatusRequestModel, checkEmailStatusResponseModel, GetAreaOfInterestResponseModel, GetImageUploadLinkRequestModel, GetImageUploadLinkResponseModel, UpdateProfileRequestModel, UpdateProfileRespModel, UserEventStatusRespModel } from './profile.model';
import { BaseResponse } from '../../shared/models/base-response.model';
import { firstValueFrom, Observable } from 'rxjs';
import { apiEndpoints } from '../../core/constants/api-strings';

@Injectable({
  providedIn: 'root'
})
export class ProfileApiService extends BaseHttpService {

  constructor() {
    super();
  }

  // Base URL for farmer-related APIs
  private readonly farmerBaseUrl = environment.farmer_base_url;

  private readonly masterBaseUrl = environment.master_base_url;

  // Logged-in farmer's ID
  private farmerId = this.storage.getLoggedUserId();

  // Event code, defaults to 'KISAN25' if not set
  private eventcode = this.storage.getEventCode() || "KISAN25";

  /**
   *
   * @param payload Payload to update profile
   * @returns Observable with update profile response
   */
  updateBasicProfile(
    payload: UpdateProfileRequestModel
  ): Observable<HttpResult<BaseResponse<UpdateProfileRespModel>>> {

    // Build the service URL using the farmer base URL, farmer ID, and profile endpoint
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.profile
      }`;

    // Make a PUT request to the constructed service URL with authentication
    return this.put<BaseResponse<UpdateProfileRespModel>>(
      serviceName,
      payload,
      {},
      true
    );
  }

  /**
   * Fetches the user's event status and profile information from the backend.
   * Constructs the API endpoint using the farmer's base URL, ID, event code, and status path.
   * @returns Observable emitting the HTTP result containing the user's event status and profile data.
   */
  getUserEventStatus(): Observable<HttpResult<BaseResponse<UserEventStatusRespModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.status
      }`;

    // Make a GET request to the constructed service URL with authentication
    return this.get<BaseResponse<UserEventStatusRespModel>>(
      serviceName,
      {},
      true
    );
  }

  /**
   * Get a signed URL for uploading an exhibitor logo.
   * Constructs the API endpoint using the farmer's base URL, ID, and the profile picture upload path.
   * Sends a POST request with the image upload payload and expects a typed response containing the signed URL and upload details.
   * @param payload Object containing details about the image to be uploaded (e.g., filename).
   * @returns Observable emitting the HTTP result with the signed upload URL and related response data.
   */
  getUploadImageLink(
    payload: GetImageUploadLinkRequestModel,
  ): Observable<HttpResult<BaseResponse<GetImageUploadLinkResponseModel>>> {

    // Build the service URL using the farmer base URL, farmer ID, and profile picture upload link endpoint
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.profile_pic_upload_link
      }`;

    // Make a POST request to the constructed service URL with authentication
    return this.post<BaseResponse<GetImageUploadLinkResponseModel>>(serviceName, payload);
  }

  /**
  * Upload logo to S3 using pre-signed URL and required fields
  */
  uploadLogoToS3Api(
    mediaUrl: string,
    fields: Record<string, string>,
    file: File
  ) {
    const formData = new FormData();

    // Append required S3 fields
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Append the file
    formData.append("file", file);

    return this.s3BucketPost<HttpResult<any>>(mediaUrl, formData);
  }

  /**
   * Checks if the provided email address is already registered or available for use.
   * Constructs the API endpoint using the farmer's base URL, ID, and the email status path.
   * Sends a POST request with the email payload and expects a typed response indicating the status.
   * @param payload Object containing the email address to check
   * @returns Observable emitting the HTTP result with the email status response
   */
  checkEmailStatus(
    payload: checkEmailStatusRequestModel
  ): Observable<HttpResult<BaseResponse<checkEmailStatusResponseModel>>> {

    // Build the service URL using the farmer base URL, farmer ID, and email status endpoint
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.check_email_status
      }`;

    // Make a POST request to the constructed service URL with authentication
    return this.post<BaseResponse<checkEmailStatusResponseModel>>(serviceName, payload, {}, true);
  }

  getAreaOfInterest() : Observable<
    HttpResult<BaseResponse<GetAreaOfInterestResponseModel>>
  > {
    // Get the endpoint path for fetching categories
    let serviceName = apiEndpoints.categories.getAllCategories;

    // Construct the full API URL
    let url = this.masterBaseUrl +  serviceName;
    // let url = this.masterBaseUrl.replace(/\/$/, '') + serviceName;

    // Make GET request using base service method (no loader by default)
    return this.get<BaseResponse<GetAreaOfInterestResponseModel>>(url, {}, false);
  }

  updateAreaOfInterest(areaOfInterestIds: string): Observable<HttpResult<BaseResponse<any>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.categories.updateCategories
      }`;

    // Prepare the request body payload
    let requestBody = {
      category_ids: areaOfInterestIds,
    };

    // Send PUT request using base service method
    // Arguments: URL, request body, options (empty), show loader = true
    return this.put(serviceName, requestBody, {}, true);
  }
}
